import { Component, OnInit, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AuthService } from 'src/app/services/auth.service';
import { NursingAuditService } from "src/app/services/nursing-audit.service";
import {
  StockAreaApiResponse,
  StockAreaFilter,
  ComplianceWindow,
  DEFAULT_COMPLIANCE_WINDOW,
  STOCK_AREA_COMPLIANCE_WINDOWS,
  StockAreaRecord
} from 'src/app/models/stock-area-config';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-stock-area-table',
  templateUrl: './stock-area-table.component.html',
  styleUrls: ['./stock-area-table.component.scss'],
  standalone: false
})
export class StockAreaTableComponent implements OnInit {
  readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly auditService = inject(NursingAuditService);

  assayManagementEnabled = environment.enableAssayManagement;
  filterPanelCollapsed = false;
  infoPanelCollapsed = true;
  showTrackableConfirmDialog = false;
  isSavingTrackableChange = false;
  isSavingComplianceWindowChange = false;
  complianceWindowOptions = [
    { label: '2 Hours', value: 2 as ComplianceWindow },
    { label: '4 Hours', value: 4 as ComplianceWindow }
  ];
  pendingStockArea: StockAreaRecord | null = null;
  pendingAssayTrackableValue = false;
  originalAssayTrackableValue = false;
  filter: any = {
    site: null,
    area: null,
    stockArea: null,
    CDA: null,
    deviceType: null,
    assayTrackable: null
  };

  filterOptions: any = {
    site: [],
    area: [],
    stockArea: [],
    CDA: [],
    deviceType: []
  };

  cols = [
    { field: 'site', header: 'Site' },
    { field: 'CDA', header: 'Custom Diversion Area' },
    { field: 'stockArea', header: 'Stock Area' },
    ...(this.assayManagementEnabled
      ? [{ field: 'assayTrackable', header: 'Assay Trackable' }]
      : []),
    { field: 'complianceTimeWindow', header: 'Compliance Window' },
    { field: 'updatedAt', header: 'Last Configuration Date', data: true, format: `MM/dd/yyyy hh:mm` }
  ];

  records: StockAreaRecord[] = [];
  ngOnInit(): void {
    this.loadStockAreaData();
  }

  loadStockAreaData(filter: StockAreaFilter = {}): void {
    this.auditService.getStockArea(filter).subscribe((res) => {
      this.loadStockAreas(res);
    });
  }

  loadStockAreas(res: StockAreaApiResponse | null): void {
    const stockAreaData = res?.stockAreaData || [];

    this.records = stockAreaData
      .map((stockArea) => {
        return {
          ...stockArea,
          assayTrackable: stockArea.assayTrackable ?? false,
          complianceTimeWindow: this.normalizeComplianceWindow(stockArea.complianceTimeWindow)
        };
      })
      .filter((stockArea) => Boolean(stockArea.site || stockArea.stockArea));
      this.buildFilterOptions(this.records);
  }

  private normalizeComplianceWindow(value: number | string | undefined): ComplianceWindow {
    const numericValue = typeof value === 'string' ? Number(value) : value;

    return STOCK_AREA_COMPLIANCE_WINDOWS.includes(numericValue as ComplianceWindow)
      ? (numericValue as ComplianceWindow)
      : DEFAULT_COMPLIANCE_WINDOW;
  }

  applyFilters(): void {
    this.loadStockAreaData(this.buildActiveFilter());
  }

  resetFilters(): void {
    this.filter = this.getEmptyFilter();
    this.loadStockAreaData();
  }

  private getEmptyFilter(): any {
    return {
      site: null,
      area: null,
      stockArea: null,
      CDA: null,
      deviceType: null,
      assayTrackable: null
    };
  }

  private buildActiveFilter(): any {
    const activeFilter: any = {};

    Object.keys(this.filter).forEach((key) => {
      const value = this.filter[key];

      if (value !== null && value !== undefined && value !== '') {
        activeFilter[key] = value;
      }
    });

    return activeFilter;
  }

  private buildFilterOptions(records: any[]): void {
    this.filterOptions = {
      site: this.createFilterOptions(records, ['site', 'Site']),
      area: this.createFilterOptions(records, ['area', 'Area', 'areaName']),
      stockArea: this.createFilterOptions(records, ['stockArea', 'Device', 'device', 'bedDev', 'loc']),
      CDA: this.createFilterOptions(records, ['CDA', 'customDiversionArea', 'customDiversionAreas']),
      deviceType: this.createFilterOptions(records, ['deviceType', 'DeviceType'])
    };
  }

  private createFilterOptions(records: any[], fields: string[]): any[] {
    const values = new Set<string>();

    records.forEach((record) => {
      fields.forEach((field) => {
        const value = record[field];

        if (value !== null && value !== undefined && String(value).trim() !== '') {
          values.add(String(value));
        }
      });
    });

    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }));
  }

  canEdit(): boolean {
    return this.getUserRole() === 'DDPD';
  }

  getUserRole(): string {
    if (this.authService.user === undefined || this.authService.user === null) {
      return '';
    }

    return this.authService.user.role;
  }

  requestAssayTrackableChange(rowData: StockAreaRecord): void {
    if (!this.assayManagementEnabled || !this.canEdit() || this.showTrackableConfirmDialog || this.isSavingTrackableChange) {
      return;
    }

    this.pendingStockArea = rowData;
    this.originalAssayTrackableValue = rowData.assayTrackable;
    this.pendingAssayTrackableValue = !this.originalAssayTrackableValue;
    this.showTrackableConfirmDialog = true;
  }

  confirmTrackableChange(): void {
    if (!this.assayManagementEnabled || !this.canEdit()) {
      this.cancelTrackableChange();
      return;
    }

    if (!this.pendingStockArea) {
      this.showTrackableConfirmDialog = false;
      return;
    }

    const pendingStockArea = this.pendingStockArea;
    this.isSavingTrackableChange = true;

    this.auditService.updateStockAreaConfig(
      pendingStockArea,
      this.pendingAssayTrackableValue,
      pendingStockArea.complianceTimeWindow
    ).subscribe(
      () => {
        pendingStockArea.assayTrackable = this.pendingAssayTrackableValue;
        pendingStockArea.updatedAt = new Date().toISOString();
        pendingStockArea.updatedBy = this.getCurrentUserName();
        this.showTrackableConfirmDialog = false;
        this.isSavingTrackableChange = false;
        this.clearPendingTrackableChange();
        this.messageService.add({
          severity: 'success',
          summary: 'Changes saved',
          detail: ''
        });
      },
      () => {
        this.isSavingTrackableChange = false;
        this.cancelTrackableChange();
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to save changes',
          detail: ''
        });
      }
    );
  }

  onComplianceWindowChange(rowData: StockAreaRecord, selectedValue: ComplianceWindow): void {
    if (!this.canEdit() || this.isSavingComplianceWindowChange) {
      return;
    }

    this.isSavingComplianceWindowChange = true;

    const previousValue = rowData.complianceTimeWindow;
    const nextValue = selectedValue;

    if (previousValue === nextValue) {
      rowData.complianceTimeWindow = previousValue;
      this.isSavingComplianceWindowChange = false;
      return;
    }

    rowData.complianceTimeWindow = nextValue;
    this.auditService.updateStockAreaConfig(rowData, rowData.assayTrackable, nextValue).subscribe(
      () => {
        rowData.updatedAt = new Date().toISOString();
        rowData.updatedBy = this.getCurrentUserName();
        this.isSavingComplianceWindowChange = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Changes saved',
          detail: ''
        });
      },
      () => {
        rowData.complianceTimeWindow = previousValue;
        this.isSavingComplianceWindowChange = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to save changes',
          detail: ''
        });
      }
    );
  }

  cancelTrackableChange(): void {
    if (this.isSavingTrackableChange) {
      return;
    }

    if (this.pendingStockArea) {
      this.pendingStockArea.assayTrackable = this.originalAssayTrackableValue;
    }

    this.showTrackableConfirmDialog = false;
    this.clearPendingTrackableChange();
  }

  private clearPendingTrackableChange(): void {
    this.pendingStockArea = null;
    this.pendingAssayTrackableValue = false;
    this.originalAssayTrackableValue = false;
  }

  private getCurrentUserName(): string {
    return this.authService.user?.fullname || this.authService.user?.email || '';
  }
}
